import { useState, useEffect } from 'react';
import { $getSelection, $isRangeSelection, $isTextNode } from 'lexical';
import { $patchStyleText } from '@lexical/selection';
import { useRichTextEditor } from '../RichTextEditorProvider';
import { Select, MenuItem, FormControl, TextField, Box, Divider } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';

const FONT_SIZES = [
  { value: 16, label: 'Small' },
  { value: 24, label: 'Medium' },
  { value: 40, label: 'Large' },
  { value: 64, label: 'Extra Large' },
  { value: 96, label: 'Huge' },
];
const DEFAULT_FONT_SIZE = FONT_SIZES[0].value;

const applyFontSizeSelectorScaleModifier = (
  size: number,
  fontSizeSelectorScaleModifier: number,
) => {
  return size * fontSizeSelectorScaleModifier;
};

const getFontSizeFromSelection = (
  editor: ReturnType<typeof useRichTextEditor>['editor'],
  fontSizeSelectorScaleModifier: number,
): number | null => {
  const editorState = editor.getEditorState();
  let foundSize: number | null = null;

  editorState.read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Get font size from selected text nodes
      const nodes = selection.getNodes();
      for (const node of nodes) {
        if ($isTextNode(node)) {
          const style = node.getStyle();
          const fontSizeMatch = style.match(/font-size:\s*([^;]+)/);
          if (fontSizeMatch) {
            const extractedSize = parseInt(fontSizeMatch[1].trim().replace('px', '').trim(), 10);
            if (!isNaN(extractedSize)) {
              foundSize = applyFontSizeSelectorScaleModifier(
                extractedSize,
                fontSizeSelectorScaleModifier,
              );
              return;
            }
          }
        }
      }
    }
  });

  return foundSize;
};

export const FontSizeSelector = () => {
  const { editor, fontSizeSelectorScaleModifier } = useRichTextEditor();

  const [fontSize, setFontSize] = useState<number>(() => {
    const currentFontSize = getFontSizeFromSelection(editor, fontSizeSelectorScaleModifier);
    return currentFontSize !== null
      ? currentFontSize
      : applyFontSizeSelectorScaleModifier(DEFAULT_FONT_SIZE, fontSizeSelectorScaleModifier);
  });
  const [customFontSize, setCustomFontSize] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      const currentFontSize = getFontSizeFromSelection(editor, fontSizeSelectorScaleModifier);
      if (currentFontSize !== null) {
        setFontSize(currentFontSize);
      } else {
        // If no font size found, use default
        setFontSize(
          applyFontSizeSelectorScaleModifier(DEFAULT_FONT_SIZE, fontSizeSelectorScaleModifier),
        );
      }
    });
  }, [editor, fontSizeSelectorScaleModifier]);

  const _roundedFontSize = Math.round(fontSize);

  // Update custom input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setCustomFontSize(_roundedFontSize.toString());
    }
  }, [isOpen, _roundedFontSize]);

  const handleFontSizeChange = (value: number | string) => {
    // Ignore the special "custom" value
    if (value === 'custom') {
      return;
    }
    const _value: number = typeof value === 'string' ? parseInt(value, 10) : value;

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Apply font size to the selected text
        $patchStyleText(selection, {
          'font-size': `${_value / fontSizeSelectorScaleModifier}px`,
        });
      } else if (selection) {
        // Collapsed selection (cursor position) - apply to current position
        $patchStyleText(selection, {
          'font-size': `${_value / fontSizeSelectorScaleModifier}px`,
        });
      }
    });
    setFontSize(Number(_value));
    setIsOpen(false);
  };

  const handleCustomFontSizeSubmit = () => {
    const size = customFontSize.trim();
    const numSize = parseInt(size, 10);
    if (size && !isNaN(numSize) && numSize > 0) {
      handleFontSizeChange(numSize);
    }
  };

  const handleCustomFontSizeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomFontSizeSubmit();
    }
  };

  return (
    <FormControl size="small" sx={{ minWidth: 120 }}>
      <Select
        value={_roundedFontSize}
        onChange={(e) => handleFontSizeChange(Number(e.target.value))}
        displayEmpty
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        sx={{
          border: 'none',
          '& fieldset': {
            border: 'none',
          },
        }}
        renderValue={(selected) => {
          const size = FONT_SIZES.find((size) => size.value === selected);
          return size ? size.label : selected.toString();
        }}
        MenuProps={{
          className: 'rich-text-editor-font-size-selector-menu rich-text-editor-toolbar-item',
          anchorOrigin: {
            vertical: -16,
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          MenuListProps: {},
          PaperProps: {},
        }}
      >
        <MenuItem value={_roundedFontSize} sx={{ display: 'none' }}>
          {_roundedFontSize}px
        </MenuItem>
        {FONT_SIZES.map((size) => (
          <MenuItem
            key={size.value}
            value={size.value}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              '&:after': {
                display: 'none',
              },
            }}
          >
            <span>{size.label}</span>
            {fontSizeSelectorScaleModifier !== 1 && (
              <Typography variant="caption" color={colors.grey[700]}>
                {size.value}px
              </Typography>
            )}
          </MenuItem>
        ))}
        <Divider sx={{ borderColor: colors.grey[120] }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <TextField
            size="small"
            value={customFontSize}
            onChange={(e) => setCustomFontSize(Math.round(Number(e.target.value)).toString())}
            onKeyDown={handleCustomFontSizeKeyDown}
            onBlur={handleCustomFontSizeSubmit}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderColor: 'primary.main',
                '& fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>
      </Select>
    </FormControl>
  );
};
